/* ============================================================
   Booking Widget — Styles (Phase 2)
   ============================================================ */

:root {
  /* ---- Typografia ---- */
  --font-family: "DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  /* ---- Základ: značková farba, text, plochy ---- */
  --color-primary: #5a7a66;           /* hlavné tlačidlá, aktívne taby, odkazy */
  --color-primary-hover: #4c6b57;
  --color-primary-disabled: #9db3a5;
  --color-primary-soft: #d5e0da;      /* svetlý podklad aktívneho výberu */
  --color-on-primary: #ffffff;        /* text na značkovej farbe */
  --color-bg: #f3ede6;                /* pozadie widgetu */
  --color-surface: #f6f1ed;           /* karty, popupy, bunky */
  --color-border: #dccdb7;
  --color-text: #212121;
  --color-text-muted: #757575;
  --color-overlay: rgba(0, 0, 0, 0.4);          /* stmavenie za modálom */
  --color-focus-ring: rgba(90, 122, 102, 0.2); /* okolo aktívneho inputu */

  /* ---- Dnešný deň ---- */
  --color-today: #ffffff;
  --color-today-border: #5a7a66;

  /* ---- Minulé dni: sivá namiesto priehľadnosti, aby sa odlíšili od plných slotov ---- */
  --color-past-text: #9a948c;
  --color-past-bg: #ebe7e1;

  /* ---- Sloty: Stôl (hnedá) ---- */
  --color-stol: #212121;              /* text dostupnosti, bodka v legende */
  --color-stol-bg: #ede6db;
  --color-stol-border: #dccdb7;
  --color-stol-label: #212121;        /* názov zdroja v riadku */

  /* ---- Sloty: Kruh (okrová) ---- */
  --color-kruh: #212121;
  --color-kruh-bg: #e5daca;
  --color-kruh-border: #dccdb7;
  --color-kruh-label: #212121;

  /* ---- Sloty: Workshop (šalviová) ---- */
  --color-workshop: #212121;
  --color-workshop-bg: #dccdb7;
  --color-workshop-border: #dccdb7;
  --color-workshop-label: #212121;

  /* ---- Sloty: plný / obsadený ---- */
  --color-full: #dccdb7;              /* text a bodka v legende */
  --color-full-bg: #f3efe8;

  /* ---- Moja rezervácia ---- */
  --color-mine: #5a7a66;
  --color-mine-bg: #abbcb2;

  /* ---- Blokovaný slot (admin) ---- */
  --color-blocked: #94a3b8;

  /* ---- Clayground: lektorka je v štúdiu (terakota, jediná "hlinená" farba v palete) ---- */
  --color-clayground: #b5654a;        /* prúžok v bunke, bodka v legende, štítok */
  --color-clayground-bg: #f0d9cd;     /* podklad štítku */

  /* ---- Admin režim ---- */
  --color-admin: #dc2626;             /* badge ADMIN, admin tab */

  /* ---- Kredit ---- */
  --color-credit: #5a7a66;
  --color-credit-bg: #abbcb2;
  --color-credit-border: #abbcb2;

  /* ---- Stavové farby ---- */
  --color-success: #5a7a66;
  --color-success-bg: #abbcb2;
  --color-warning: #212121;
  --color-warning-bg: #dccdb7;
  --color-warning-border: #c4b399;
  --color-danger: #dc2626;            /* chyby, zrušenie, mazanie */
  --color-danger-hover: #b91c1c;
  --color-danger-bg: #fee2e2;
  --color-danger-border: #f5c6c2;

  /* ---- Tvary ---- */
  --radius: 6px;
  --shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  --shadow-lg: 0 10px 25px rgba(0, 0, 0, 0.12);
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: var(--font-family);
  background: var(--color-bg);
  color: var(--color-text);
  line-height: 1.5;
}

/* Widget shell */
.booking-widget {
  max-width: 1800px;
  margin: 0 auto;
  padding: 16px 24px;
}

.widget-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: var(--color-text-muted);
}

/* Header */
.widget-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--color-border);
  margin-bottom: 16px;
}

.widget-header h1 {
  font-size: 1.4rem;
  font-weight: 900;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

.user-info {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--color-text);
}

.user-code {
  color: var(--color-text-muted);
  font-weight: 500;
}

.tab-nav {
  display: flex;
  gap: 4px;
}

.tab-btn {
  padding: 6px 14px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  border-radius: var(--radius);
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--color-text-muted);
  transition: all 0.15s;
}

.tab-btn.active {
  background: var(--color-primary);
  color: var(--color-on-primary);
  border-color: var(--color-primary);
}

/* Buttons */
.btn {
  padding: 8px 16px;
  border: none;
  border-radius: var(--radius);
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: background-color 0.15s, opacity 0.15s;
  -webkit-tap-highlight-color: transparent;
  color: var(--color-text);
}

.btn:disabled {
  cursor: not-allowed;
}

.btn-primary {
  background: var(--color-primary);
  color: var(--color-on-primary);
}

.btn-primary:disabled {
  background: var(--color-primary-disabled);
  color: var(--color-on-primary);
  opacity: 0.85;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-primary-hover);
}

.btn-secondary {
  background: var(--color-bg);
  color: var(--color-text);
  border: 1px solid var(--color-border);
}

.btn-secondary:disabled {
  color: var(--color-text-muted);
}

.btn-small {
  padding: 4px 12px;
  font-size: 0.8rem;
}

.btn-icon {
  padding: 4px 10px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  cursor: pointer;
  font-size: 1rem;
}

.btn-outline {
  background: transparent;
  border: 1px solid var(--color-border);
  color: var(--color-text-muted);
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--color-text-muted);
  padding: 0;
  line-height: 1;
}

.link-btn {
  background: none;
  border: none;
  color: var(--color-primary);
  cursor: pointer;
  font-size: inherit;
  text-decoration: underline;
  padding: 0;
}

/* Calendar */
.calendar {
  background: var(--color-surface);
  border-radius: var(--radius);
  border: 1px solid var(--color-border);
  padding: 16px;
}

/* Sticky navigation + time header while scrolling the week.
   --sticky-top: height of a sticky host header (set by embed.tsx on Hostinger, 0 in the SPA).
   --cal-nav-h: measured height of .cal-nav (set by Calendar.tsx), so the time row sticks right under it.
   Negative side margins + padding let the bar cover the .calendar inner padding, so cells
   don't peek out beside it. */
.cal-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  position: sticky;
  top: var(--sticky-top, 0px);
  z-index: 5;
  background: var(--color-surface);
  margin: -16px -16px 0;
  padding: 16px 16px 12px;
}

.cal-week-label {
  font-weight: 700;
  font-size: 0.95rem;
  flex: 1;
  text-align: center;
}

.empty-week {
  text-align: center;
  padding: 40px 16px;
  color: var(--color-text-muted);
  font-size: 0.95rem;
}

/* Desktop grid */
.cal-grid {
  overflow-x: clip; /* not `hidden`: that would trap the sticky thead inside */
}

.cal-grid thead th {
  position: sticky;
  top: calc(var(--sticky-top, 0px) + var(--cal-nav-h, 0px));
  z-index: 4;
  background: var(--color-surface);
}

.cal-grid table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  border-spacing: 0;
  table-layout: fixed;
}

.cal-corner {
  width: 70px;
}

.cal-resource-corner {
  width: 120px;
}

.cal-resource-label {
  padding: 18px 10px 18px 4px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--color-text);
  white-space: nowrap;
  vertical-align: middle;
  text-align: left;
  background: var(--color-surface);
}

.cal-time-header {
  padding: 8px 4px 6px;
  font-weight: 500;
  text-align: center;
  font-size: 0.75rem;
  color: var(--color-text-muted);
  white-space: nowrap;
  border-bottom: 1px solid var(--color-border);
}

.cal-day-label {
  padding: 0 8px;
  vertical-align: middle;
  border-top: 10px solid transparent;
  background-image: linear-gradient(var(--color-border), var(--color-border));
  background-size: 100% 2px;
  background-repeat: no-repeat;
  background-position: top;
}

.cal-day-label .day-name {
  font-weight: 700;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 6px;
}

.cal-day-label .day-date {
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

.row-today td {
  background-color: var(--color-today) !important;
  background-clip: padding-box;
}

/* Posledný riadok dňa (kruh, alebo workshop ak existuje) dotiahne biely podklad
   až po deliacu čiaru. Tabuľka má border-collapse, takže bez tohto by cez
   priehľadný horný okraj ďalšieho dňa presvital podklad tabuľky. */
.row-today.row-kruh > td,
.row-today.row-workshop > td {
  border-bottom: 10px solid var(--color-today);
}

.row-today.row-stol > .cal-day-label {
  border-bottom: 10px solid var(--color-today);
}

/* Bunky majú 1 px priehľadný horný okraj; v bielom dnešnom riadku by cez
   neho presvital béžový podklad tabuľky ako tenká čiara medzi riadkami. */
.row-today:not(.row-stol) > td { border-top-color: var(--color-today); }

.row-today .cell-available { background: transparent; }
.row-today .cell-full { background: transparent; }


.cal-cell {
  padding: 18px 2px;
  height: 48px;
  text-align: center;
  border-top: 1px solid transparent;
  vertical-align: middle;
  transition: all 0.1s;
  overflow: hidden;
  background: transparent;
}

/* First resource row per day gets a gap + line separator */
.row-stol > .cal-cell,
.row-stol > .cal-resource-label {
  border-top: 10px solid transparent;
  background-image: linear-gradient(var(--color-border), var(--color-border));
  background-size: 100% 2px;
  background-repeat: no-repeat;
  background-position: top;
}

.cell-resource-tag {
  display: block;
  font-size: 0.65rem;
  color: var(--color-text-muted);
  line-height: 1;
}

.cell-avail {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 0.8rem;
  border-radius: 8px;
  height: 36px;
  margin: 0 2px;
}

/* Full (no capacity) — grey for all resources */
.cell-full .cell-avail {
  background: var(--color-full-bg);
  color: var(--color-full);
}

/* Stôl available — warm brown */
.row-stol .cell-available .cell-avail {
  background: var(--color-stol-bg);
  color: var(--color-stol);
}

/* Kruh available — soft yellow */
.row-kruh .cell-available .cell-avail {
  background: var(--color-kruh-bg);
  color: var(--color-kruh);
}

/* Past days: grey tokens instead of opacity, so past slots don't look like full ones.
   Must come after the per-resource rules above and match their specificity, or it loses. */
.row-past .cal-day-label,
.row-past .ws-cell-title {
  color: var(--color-past-text);
}

.row-past .cell-available .cell-avail,
.row-past .cell-full .cell-avail,
.row-past .cell-workshop-span .ws-pill {
  background: var(--color-past-bg);
  color: var(--color-past-text);
  border-color: var(--color-past-bg);
}

/* Workshop — sage green */
.cell-workshop {
  background: transparent;
}

.cell-empty {
  background: transparent;
}

/* My booked slots — bordová (--color-mine) */
.cell-mine {
  background-color: transparent !important;
  opacity: 1 !important;
}

.cell-mine .cell-avail {
  background: var(--color-mine-bg) !important;
  color: var(--color-mine) !important;
  font-weight: 700;
  border: 2.5px solid var(--color-mine);
}


.cell-mine-check {
  display: block;
  font-size: 0.6rem;
  font-weight: 900;
  color: var(--color-primary);
  line-height: 1;
  margin-bottom: 1px;
}

/* Day view mine badge */
.mine-badge {
  font-size: 0.7rem;
  font-weight: 900;
  color: var(--color-mine);
  background: var(--color-mine-bg);
  padding: 1px 6px;
  border-radius: 3px;
  white-space: nowrap;
}

/* Workshop chip mine */
.chip-mine {
  background: var(--color-mine-bg) !important;
  color: var(--color-mine) !important;
  border-color: var(--color-mine) !important;
}

/* Legend dot mine */
.dot-mine {
  background: var(--color-mine-bg);
  border-color: var(--color-mine);
}

.clickable {
  cursor: pointer;
}

.clickable:hover {
  filter: brightness(0.95);
}

/* Workshop row */
.cell-workshop-row {
  background: var(--color-bg);
  padding: 6px 8px;
  text-align: left;
}

/* Workshop cells aligned to time grid */
.cell-workshop-span {
  background: transparent;
  border: none;
  padding: 4px 2px;
  max-width: 0;
}

.cell-workshop-span .ws-pill {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: var(--color-workshop-bg);
  border-radius: 8px;
  height: 36px;
  padding: 0 10px;
  overflow: hidden;
}

.ws-cell-title {
  font-weight: 500;
  font-size: 0.75rem;
  color: var(--color-workshop);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ws-cell-avail {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  font-weight: 500;
  flex-shrink: 0;
}

.cell-workshop-span.cell-mine .ws-pill {
  background: var(--color-mine-bg);
  border: 2.5px solid var(--color-mine);
}

.cell-workshop-span.cell-mine .ws-cell-title {
  color: var(--color-mine);
}

.cell-avail-blocked {
  background: repeating-linear-gradient(
    -45deg,
    var(--color-full-bg),
    var(--color-full-bg) 3px,
    var(--color-border) 3px,
    var(--color-border) 4px
  ) !important;
}

/* Resource label colors matching their theme */
.row-stol .cal-resource-label { color: var(--color-stol-label); }
.row-kruh .cal-resource-label { color: var(--color-kruh-label); }
.cal-resource-label-ws { color: var(--color-workshop-label); }
.row-past .cal-resource-label,
.row-past .cal-resource-label-ws { color: var(--color-past-text); }

.workshop-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  background: var(--color-surface);
  color: var(--color-workshop);
  border: 1px solid var(--color-workshop);
  border: none;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  margin: 2px 4px;
  transition: all 0.15s;
}

.workshop-chip:hover:not(:disabled) {
  filter: brightness(0.85);
}

.workshop-chip.chip-full {
  opacity: 0.5;
}

.chip-time {
  font-size: 0.7rem;
  opacity: 0.85;
}

/* Legend */
.cal-legend {
  display: flex;
  gap: 16px;
  margin-top: 12px;
  font-size: 0.8rem;
  color: var(--color-text-muted);
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  border: 1px solid var(--color-border);
}

.dot-full { background: var(--color-full-bg); }
.dot-clayground { background: var(--color-clayground); border-color: var(--color-clayground); }

/* Clayground: terracotta stripe on the left of the availability pill / day card */
.cal-cell.cell-clayground .cell-avail {
  box-shadow: inset 4px 0 0 var(--color-clayground);
  padding-left: 4px;
}

.cell-clayground-mark {
  display: none; /* the stripe carries the meaning; kept for screen readers */
}

.day-slot-card.cell-clayground {
  box-shadow: inset 5px 0 0 var(--color-clayground);
}

.day-slot-clayground,
.clayground-tag {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  background: var(--color-clayground-bg);
  color: var(--color-clayground);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  vertical-align: middle;
}

.popup-clayground {
  color: var(--color-clayground);
  font-weight: 500;
}

.btn-clayground {
  background: var(--color-clayground);
  color: var(--color-on-primary);
}

.btn-clayground:hover:not(:disabled) {
  background: #9e563d;
}

/* View toggle (week/day) */
.view-toggle {
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.view-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 10px;
  border: none;
  background: var(--color-surface);
  cursor: pointer;
  color: var(--color-text-muted);
  transition: all 0.15s;
}

.view-toggle-btn + .view-toggle-btn {
  border-left: 1px solid var(--color-border);
}

.view-toggle-btn.active {
  background: var(--color-primary);
  color: var(--color-on-primary);
}

.view-toggle-btn:hover:not(.active) {
  background: var(--color-bg);
}

.view-toggle-btn svg {
  width: 16px;
  height: 16px;
}

/* Day tabs (shared between desktop day view and mobile) */
.day-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
  overflow-x: auto;
  position: sticky;
  top: calc(var(--sticky-top, 0px) + var(--cal-nav-h, 0px));
  z-index: 4;
  background: var(--color-surface);
  padding-bottom: 4px;
}

.day-tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: var(--color-surface);
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
  position: relative;
  color: var(--color-text);
}

.day-tab.active {
  background: var(--color-primary);
  color: var(--color-on-primary);
  border-color: var(--color-primary);
}

.day-tab.tab-past:not(.active) {
  background: var(--color-past-bg);
  color: var(--color-past-text);
  border-color: var(--color-past-bg);
}

.day-tab.tab-today:not(.active) {
  border-color: var(--color-today-border);
  border-width: 2px;
}

.tab-today-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--color-today-border);
  position: absolute;
  bottom: 2px;
}

.day-tab.active .tab-today-dot {
  background: var(--color-on-primary);
}

.tab-day { font-size: 0.7rem; font-weight: 700; }
.tab-date { font-size: 0.85rem; }

/* Day slot cards (shared between desktop day view and mobile) */
.day-slots {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.day-slot-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: var(--radius);
  border: 1px solid var(--color-border);
  font-size: 0.85rem;
}

/* Day view resource colors */
.day-slot-card.res-stol.cell-available {
  background: var(--color-stol-bg);
  border-color: var(--color-stol-border);
}
.day-slot-card.res-stol.cell-available .day-slot-type { color: var(--color-stol-label); }
.day-slot-card.res-stol.cell-available .day-slot-avail { color: var(--color-stol); }

.day-slot-card.res-kruh.cell-available {
  background: var(--color-kruh-bg);
  border-color: var(--color-kruh-border);
}
.day-slot-card.res-kruh.cell-available .day-slot-type { color: var(--color-kruh-label); }
.day-slot-card.res-kruh.cell-available .day-slot-avail { color: var(--color-kruh); }

.day-slot-card.cell-full {
  background: var(--color-full-bg);
  border-color: var(--color-border);
  opacity: 0.6;
}
.day-slot-card.cell-full .day-slot-type,
.day-slot-card.cell-full .day-slot-avail { color: var(--color-full); }

.day-slot-card.cell-workshop {
  background: var(--color-workshop-bg);
  border-color: var(--color-workshop-border);
}
.day-slot-card.cell-workshop .day-slot-type { color: var(--color-workshop-label); }
.day-slot-card.cell-workshop .day-slot-avail { color: var(--color-workshop); }

/* Day view past: whole day is history, every card goes grey */
.day-slots-past .day-slot-card {
  background: var(--color-past-bg);
  border-color: var(--color-past-bg);
  opacity: 1;
}
.day-slots-past .day-slot-card .day-slot-type,
.day-slots-past .day-slot-card .day-slot-avail,
.day-slots-past .day-slot-card .day-slot-time,
.day-slots-past .day-slot-card .day-slot-price { color: var(--color-past-text); }

/* Day view mine */
.day-slot-card.cell-mine {
  background: var(--color-mine-bg) !important;
  border-color: var(--color-mine) !important;
  border-width: 2px;
}
.day-slot-card.cell-mine .day-slot-type,
.day-slot-card.cell-mine .day-slot-avail { color: var(--color-mine) !important; }

.day-slot-time {
  font-weight: 700;
  min-width: 50px;
}

.day-slot-type {
  flex: 1;
}

.day-slot-avail {
  font-weight: 700;
  min-width: 35px;
  text-align: center;
}

.day-slot-price {
  color: var(--color-text-muted);
  font-size: 0.8rem;
  min-width: 35px;
  text-align: right;
}

/* Desktop day view — larger, centered layout */
.cal-day-view .day-tabs {
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
}

.cal-day-view .day-tab {
  padding: 8px 16px;
}

.cal-day-view .day-tab .tab-day {
  font-size: 0.8rem;
}

.cal-day-view .day-tab .tab-date {
  font-size: 1rem;
}

.cal-day-view .day-slots {
  max-width: 800px;
  margin: 0 auto;
  gap: 8px;
}

.cal-day-view .day-slot-card {
  padding: 14px 20px;
  font-size: 0.95rem;
}

.cal-day-view .day-slot-time {
  font-size: 1rem;
  min-width: 70px;
}

.cal-day-view .day-slot-price {
  font-size: 0.9rem;
}

/* Notification */
.notification {
  background: var(--color-primary);
  color: var(--color-on-primary);
  padding: 10px 16px;
  border-radius: var(--radius);
  margin-bottom: 12px;
  font-weight: 500;
  text-align: center;
  animation: fadeIn 0.3s;
  font-size: 0.9rem;
}

/* Login hint */
.login-hint {
  text-align: center;
  padding: 16px;
  color: var(--color-text-muted);
  font-size: 0.9rem;
}

/* Loading */
.loading {
  text-align: center;
  padding: 40px;
  color: var(--color-text-muted);
}

/* Auth modal overlay — in App.tsx now */
.auth-modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--color-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  animation: fadeIn 0.2s;
}

.auth-form {
  background: var(--color-surface);
  border-radius: 12px;
  padding: 32px;
  width: 100%;
  max-width: 400px;
  box-shadow: var(--shadow-lg);
  margin: 16px;
}

.auth-form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.auth-form-header h2 {
  font-size: 1.3rem;
  margin: 0;
}

.form-field {
  margin-bottom: 14px;
}

.form-field label {
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: 4px;
  color: var(--color-text-muted);
}

.form-field input[type="text"],
.form-field input[type="email"],
.form-field input[type="password"] {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  font-size: 0.9rem;
  transition: border-color 0.15s;
}

.form-field input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-focus-ring);
}

.form-field.checkbox label {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
}

.form-field.checkbox input[type="checkbox"] {
  margin-top: 3px;
}

.form-field.checkbox a {
  color: var(--color-primary);
  text-decoration: underline;
}

.form-error {
  color: var(--color-danger);
  background: var(--color-danger-bg);
  padding: 8px 12px;
  border-radius: var(--radius);
  font-size: 0.85rem;
  margin-bottom: 12px;
}

.form-success {
  color: var(--color-success);
  background: var(--color-success-bg);
  padding: 8px 12px;
  border-radius: var(--radius);
  font-size: 0.85rem;
  margin-bottom: 12px;
}

.auth-form .btn-primary {
  width: 100%;
  padding: 10px;
  font-size: 0.95rem;
}

.auth-forgot {
  text-align: center;
  margin-top: 8px;
  font-size: 0.85rem;
}

.auth-switch {
  text-align: center;
  margin-top: 16px;
  font-size: 0.85rem;
  color: var(--color-text-muted);
}

/* Booking popup */
.popup-overlay {
  position: fixed;
  inset: 0;
  background: var(--color-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  animation: fadeIn 0.2s;
}

.popup {
  background: var(--color-surface);
  border-radius: 12px;
  padding: 28px;
  width: 100%;
  max-width: 380px;
  box-shadow: var(--shadow-lg);
  margin: 16px;
}

.popup h3 {
  font-size: 1.2rem;
  margin-bottom: 16px;
}

/* Okno rezervácie: popisky a hodnoty ako na účtenke, bez ikon */
.popup-head { display: flex; flex-direction: column; gap: 2px; padding-bottom: 14px; border-bottom: 1px solid var(--color-border); }
.popup-head h3 { margin: 0; }
.popup-eyebrow { font-size: 0.7rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--color-text-muted); }
.popup-sub { color: var(--color-text-muted); font-size: 0.9rem; }

.popup-grid {
  display: grid;
  grid-template-columns: 6.5em 1fr;
  align-items: center;
  row-gap: 10px;
  column-gap: 12px;
  margin: 0;
  padding: 14px 0;
  font-size: 0.95rem;
}
.popup-grid dt { color: var(--color-text-muted); font-size: 0.85rem; }
.popup-grid dd { margin: 0; }
.popup-grid .time-select, .popup-grid .person-select { margin-left: 0; }

.popup-occupancy { display: flex; align-items: center; gap: 10px; color: var(--color-text-muted); font-size: 0.9rem; }
.popup-bar { flex: 1; height: 3px; background: var(--color-border); border-radius: 2px; overflow: hidden; }
.popup-bar-fill { display: block; height: 100%; background: var(--color-primary); }

.popup-sum { border-top: 1px solid var(--color-border); row-gap: 6px; margin-bottom: 20px; }
.popup-sum dd { text-align: right; }
.popup-sum dt { align-self: start; padding-top: 2px; } /* popisok pri dvojriadkovej cene sedí k sume */
.popup-sum .price-detail { display: block; font-weight: 500; font-size: 0.8rem; white-space: nowrap; margin-top: 2px; }
.popup-total { font-size: 1.15rem; font-weight: 900; }
.popup-credit { color: var(--color-credit); }
.popup-discount { color: var(--color-credit); }
.popup-discount .link-btn { font-size: inherit; }

/* Discount code field under the price */
.popup-discount-code { margin: -8px 0 16px; font-size: 0.9rem; }
.popup-discount-row { display: flex; gap: 8px; }
.popup-discount-row input {
  flex: 1;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  font-size: 0.9rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  background: var(--color-surface);
}
.popup-discount-row input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-focus-ring);
}
.popup-discount-code .form-error { margin-top: 6px; }
.popup-due { font-weight: 700; }
.popup-full { color: var(--color-text-muted); padding: 0 0 16px; margin: 0; border-top: 1px solid var(--color-border); padding-top: 14px; }

/* Time range selector in popup */
.time-select {
  padding: 4px 8px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  font-size: 0.95rem;
  background: var(--color-surface);
  cursor: pointer;
  margin-left: 4px;
}

.time-select:focus {
  outline: none;
  border-color: var(--color-primary);
}

.person-select {
  padding: 4px 8px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  font-size: 0.95rem;
  background: var(--color-surface);
  cursor: pointer;
  margin-left: 4px;
}

.person-select:focus {
  outline: none;
  border-color: var(--color-primary);
}

.price-detail {
  color: var(--color-text-muted);
  font-size: 0.85rem;
}

.popup-login-hint {
  text-align: center;
  padding: 10px;
  margin-bottom: 16px;
  background: var(--color-bg);
  border-radius: var(--radius);
  color: var(--color-text-muted);
  font-size: 0.9rem;
}

.popup-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

/* My Bookings */
.my-bookings {
  background: var(--color-surface);
  border-radius: var(--radius);
  border: 1px solid var(--color-border);
  padding: 20px;
}

.my-bookings h2 {
  font-size: 1.2rem;
  margin-bottom: 8px;
}

.my-bookings h3 {
  font-size: 0.95rem;
  color: var(--color-text-muted);
  margin: 16px 0 8px;
}

.customer-code {
  font-size: 0.9rem;
  color: var(--color-text-muted);
  margin-bottom: 12px;
  padding: 8px 12px;
  background: var(--color-bg);
  border-radius: var(--radius);
}

.profile-box {
  padding: 14px 16px;
  background: var(--color-bg);
  border-radius: var(--radius);
  margin-bottom: 16px;
  border: 1px solid var(--color-border);
}

.profile-name {
  font-weight: 700;
  font-size: 1.05rem;
  margin-bottom: 2px;
}

.profile-email {
  font-size: 0.85rem;
  color: var(--color-text-muted);
  margin-bottom: 6px;
}

.profile-code {
  font-size: 0.85rem;
  color: var(--color-text-muted);
}

.profile-credit {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--color-border);
  font-size: 0.9rem;
  color: var(--color-credit);
}

.booking-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Historické rezervácie: každá časť dátumu a času má vlastný stĺpec mriežky,
   lebo DM Sans nemá tabuľkové číslice a číslice majú rôznu šírku. */
.booking-table {
  gap: 0;
  border-top: 1px solid var(--color-border);
}

.booking-card {
  display: grid;
  /* posledný stĺpec: tlačidlá pri nadchádzajúcich, v histórii prázdny, aby ceny sedeli pod sebou.
     Široký na dve tlačidlá (nezaplatená: Zaplatiť + Zrušiť), inak pretečú cez cenu (2026-10-27) */
  grid-template-columns: 1.6em 3.6em 3.1em 0.6em 3.1em 6em minmax(0, 1fr) 4em 5em 11.5em;
  grid-template-areas: "wd date start dash end kind type persons price actions";
  align-items: center;
  column-gap: 20px;
  padding: 9px 12px;
  border-bottom: 1px solid var(--color-border);
  font-size: 0.85rem;
}

/* Nadchádzajúce: tá istá mriežka + stĺpec s tlačidlami, názov tučný */
.booking-card-upcoming {
  border-bottom: none;
}
.booking-card-upcoming .booking-type { white-space: normal; display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.booking-hours { color: var(--color-text-muted); }
.booking-actions { grid-area: actions; display: flex; gap: 8px; align-items: center; justify-content: flex-end; }

.booking-weekday { grid-area: wd; }
.booking-date { grid-area: date; text-align: right; }
.booking-time-start { grid-area: start; text-align: right; color: var(--color-text-muted); }
.booking-time-dash { grid-area: dash; text-align: center; color: var(--color-text-muted); }
.booking-time-end { grid-area: end; color: var(--color-text-muted); }
.booking-kind { grid-area: kind; color: var(--color-text-muted); margin-left: 12px; white-space: nowrap; }
.booking-type { grid-area: type; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.booking-persons { grid-area: persons; color: var(--color-text-muted); white-space: nowrap; text-align: right; }
.booking-price { grid-area: price; }

/* Kredit: zostatok a transakcie v tej istej mriežke ako rezervácie */
/* Zostatok ako nadpis sekcie (rovnaký štýl ako Nadchádzajúce / Historické), suma v stĺpci cien */
.credit-balance-heading {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0 calc(11.5 * 0.85rem + 32px) 0 12px; /* stĺpec tlačidiel (11.5em pri 0.85rem) + gap + padding riadku */
}
.credit-balance-heading strong { color: var(--color-credit); font-size: 1.1rem; font-weight: 900; }
.credit-note { color: var(--color-text-muted); font-size: 0.8rem; margin: 0 0 10px 12px; }

.booking-card-credit {
  grid-template-areas: "wd date type type type type type type price actions";
}
.booking-card-credit .booking-type { display: flex; flex-wrap: wrap; align-items: baseline; gap: 2px 8px; white-space: normal; }

.credit-amount.positive { color: var(--color-success); }
.credit-amount.negative { color: var(--color-past-text); } /* záporné pohyby svetlosivé, nie červené (dizajnérka) */


@media (max-width: 600px) {
  .booking-card {
    column-gap: 8px;
    grid-template-columns: 1.6em 3.6em 3.1em 0.6em 3.1em minmax(0, 1fr) 4.2em;
    grid-template-areas:
      "wd date start dash end persons price"
      "kind kind type type type type type"
      "actions actions actions actions actions actions actions";
    row-gap: 2px;
  }
  .booking-kind { margin-left: 0; }
  .booking-actions:not(:empty) { margin-top: 6px; }
  /* kredit: typ a popis rezervácie vždy pod sebou, nie natlačené v jednom riadku */
  .booking-card-credit .booking-type { flex-direction: column; align-items: flex-start; gap: 0; }
  .booking-card-credit {
    grid-template-areas:
      "wd date . . . . price"
      "type type type type type type type";
  }
  .credit-balance-heading { padding-right: 12px; }
  .booking-type { margin-left: 0; }
}

.booking-price {
  font-weight: 700;
  color: var(--color-text-muted);
  text-align: right;
  white-space: nowrap;
}

/* Discount code next to the booking's name in Moje rezervácie (same line, row height unchanged) */
.booking-discount {
  margin-left: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--color-primary);
}

.empty {
  color: var(--color-text-muted);
  font-size: 0.85rem;
  padding: 8px 0;
}

/* Responsive */
.desktop-only { display: block; }
.desktop-only.view-toggle { display: flex; }
.mobile-only { display: none; }

@media (max-width: 1000px) {
  .desktop-only { display: none; }
  .desktop-only.view-toggle { display: none; }
  .mobile-only { display: block; }

  .widget-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-right {
    width: 100%;
    justify-content: space-between;
  }

  .cal-nav {
    flex-wrap: wrap;
    justify-content: center;
  }
}

/* Large screens — scale up for readability */
@media (min-width: 1200px) {
  .booking-widget {
    padding: 24px 40px;
  }

  .widget-header h1 {
    font-size: 1.6rem;
  }

  .cal-grid table {
    font-size: 0.9rem;
  }

  .cal-time-header {
    font-size: 0.85rem;
    padding: 6px 4px;
  }

  .cal-day-label .day-name {
    font-size: 0.95rem;
  }

  .cal-day-label .day-date {
    font-size: 0.85rem;
  }

  .cal-cell {
    padding: 10px 4px;
  }

  .cell-avail {
    font-size: 0.85rem;
  }

  .cal-resource-label {
    font-size: 0.85rem;
    padding: 10px 12px 10px 4px;
  }

  .cal-corner {
    width: 110px;
    min-width: 110px;
  }

  .cal-nav {
    font-size: 1rem;
  }

  .cal-week-label {
    font-size: 1.05rem;
  }

  .view-toggle-btn {
    padding: 6px 12px;
  }

  .view-toggle-btn svg {
    width: 18px;
    height: 18px;
  }

  .cal-day-view .day-slots {
    max-width: 900px;
  }

  .cal-day-view .day-slot-card {
    padding: 16px 24px;
    font-size: 1rem;
  }
}

/* Extra-large screens — even more breathing room */
@media (min-width: 1600px) {
  .booking-widget {
    padding: 32px 60px;
  }

  .cal-grid table {
    font-size: 0.95rem;
  }

  .cal-cell {
    padding: 10px 6px;
  }

  .cal-corner {
    width: 100px;
  }

  .cal-day-label {
    padding: 10px 8px;
  }

  .widget-header h1 {
    font-size: 1.75rem;
  }

  .popup {
    max-width: 480px;
  }

  .auth-form {
    max-width: 440px;
  }

  .cal-day-view .day-slots {
    max-width: 1000px;
  }
}

/* ============================================================
   Admin Styles — Phase 3
   ============================================================ */

/* Admin badge in header */
.admin-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 8px;
  font-size: 0.7rem;
  font-weight: 700;
  background: var(--color-admin);
  color: var(--color-on-primary);
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.tab-btn-admin {
  border-color: var(--color-admin);
  color: var(--color-admin);
}

.tab-btn-admin.active {
  background: var(--color-admin);
  color: var(--color-on-primary);
  border-color: var(--color-admin);
}

/* Admin cards (forms) */
.admin-card {
  max-width: 600px;
}

.admin-card h3 {
  font-size: 1.15rem;
  margin-bottom: 16px;
}

.admin-card .form-field {
  margin-bottom: 14px;
}

.admin-card .form-field label {
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: 4px;
  color: var(--color-text-muted);
}

.admin-card .form-field input[type="text"],
.admin-card .form-field input[type="date"],
.admin-card .form-field input[type="time"],
.admin-card .form-field input[type="number"],
.admin-card .form-field select {
  width: 100%;
  /* Explicit height: WebKit renders date/time pickers taller than text inputs,
     which breaks alignment of fields sitting next to each other in .form-row. */
  height: 40px;
  box-sizing: border-box;
  padding: 0 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  font-size: 0.9rem;
  font-family: inherit;
}

.admin-card .form-field select {
  background: var(--color-surface);
}

.admin-card .form-field input:focus,
.admin-card .form-field select:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-focus-ring);
}

.form-row {
  display: flex;
  gap: 12px;
}

/* Resource picker buttons */
.resource-picker {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
}

.resource-pick-btn {
  padding: 8px 16px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: var(--color-surface);
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.15s;
  color: var(--color-text-muted);
}

.resource-pick-btn.active {
  border-color: var(--color-primary);
  background: var(--color-primary-soft);
  color: var(--color-primary);
  font-weight: 700;
}

.resource-pick-btn:hover:not(.active) {
  background: var(--color-bg);
}

.form-hint {
  display: block;
  font-size: 0.78rem;
  color: var(--color-text-muted);
  margin-top: 2px;
}

.form-row .form-field {
  flex: 1;
}

/* Schedule info box */
.admin-schedule-info {
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  padding: 14px;
  margin-bottom: 14px;
}

.admin-schedule-info h4 {
  font-size: 0.85rem;
  margin-bottom: 8px;
  color: var(--color-text-muted);
}

.schedule-table {
  width: auto;
  border-collapse: collapse;
  font-size: 0.85rem;
  margin-bottom: 8px;
}

.schedule-table td {
  padding: 2px 6px 2px 0;
  white-space: nowrap;
}

.schedule-label {
  font-weight: 700;
  padding-right: 12px !important;
}

.schedule-select {
  padding: 4px 6px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 0.85rem;
  background: var(--color-surface);
  cursor: pointer;
}

.schedule-select:focus {
  outline: none;
  border-color: var(--color-primary);
}

.schedule-dash {
  text-align: center;
  padding: 0 6px !important;
  color: var(--color-text-muted);
}

.schedule-count {
  color: var(--color-text-muted);
  font-size: 0.8rem;
  padding-left: 12px;
  white-space: nowrap;
}

.schedule-types {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 0.85rem;
  color: var(--color-text-muted);
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--color-border);
}

.resource-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.resource-row label {
  display: flex;
  align-items: center;
  gap: 4px;
}

.resource-label {
  font-weight: 700;
  min-width: 60px;
}

.resource-input {
  width: 52px;
  padding: 3px 6px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 0.85rem;
  background: var(--color-surface);
  text-align: center;
}

.resource-input:focus {
  outline: none;
  border-color: var(--color-primary);
}

.admin-warning {
  padding: 10px 14px;
  background: var(--color-warning-bg);
  border: 1px solid var(--color-warning-border);
  border-radius: var(--radius);
  font-size: 0.85rem;
  margin-bottom: 14px;
  color: var(--color-warning);
}

.admin-result {
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: var(--radius);
  font-size: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.admin-result.success {
  background: var(--color-surface);
  color: var(--color-success);
}

.admin-result.warning {
  background: var(--color-warning-bg);
  color: var(--color-warning);
}

/* Blocked slots in calendar grid */
.cell-blocked-icon {
  display: none;
}

.chip-blocked {
  background: var(--color-blocked) !important;
  opacity: 0.7;
}

/* Booking badge in cell (admin only) */
.cal-cell {
  position: relative;
}

/* Day view blocked + badge labels */
.day-slot-blocked-label {
  color: var(--color-blocked);
  font-size: 0.8rem;
  font-style: italic;
  margin-left: auto;
}

.day-slot-badge {
  font-size: 0.75rem;
  color: var(--color-primary);
  font-weight: 700;
  white-space: nowrap;
}

/* Legend blocked dot */
.dot-blocked {
  background: var(--color-blocked);
}

/* Admin popup (wider than regular) */
.admin-popup {
  max-width: 460px;
}

.blocked-tag {
  display: inline-block;
  margin-left: 8px;
  font-size: 0.7rem;
  color: var(--color-danger);
  font-weight: 700;
}

.admin-confirm-warning {
  background: var(--color-warning-bg);
  border: 1px solid var(--color-warning-border);
  border-radius: var(--radius);
  padding: 12px;
  margin-bottom: 12px;
  font-size: 0.85rem;
  color: var(--color-warning);
}

.admin-confirm-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.btn-danger {
  background: var(--color-danger);
  color: var(--color-on-primary);
  border: none;
}

.btn-danger:hover:not(:disabled) {
  background: var(--color-danger-hover);
}

/* Disabled red/terracotta buttons used to look exactly like active ones (QA 36):
   Zablokovať on a slot with bookings looked clickable. */
.btn-danger:disabled,
.btn-clayground:disabled {
  opacity: 0.45;
}

/* Capacity inline edit */
.capacity-edit {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.capacity-input {
  width: 60px;
  padding: 4px 8px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  font-size: 0.85rem;
  text-align: center;
}

.capacity-edit-btn {
  margin-left: 2px;
  font-size: 0.75rem;
  padding: 2px 8px;
  flex: none;
}

/* Admin booking list in popup */
.admin-bookings-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--color-border);
}

.admin-bookings-section h4 {
  font-size: 0.9rem;
  margin-bottom: 8px;
  color: var(--color-text-muted);
}

.admin-booking-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.admin-booking-item {
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  background: var(--color-bg);
  border-radius: var(--radius);
  font-size: 0.8rem;
  gap: 8px;
}

.admin-booking-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.admin-booking-name {
  font-weight: 700;
}

.admin-booking-email {
  color: var(--color-text-muted);
  font-size: 0.75rem;
}

.admin-booking-code {
  color: var(--color-primary);
  font-size: 0.75rem;
  font-weight: 500;
}

.admin-booking-group-badge {
  font-size: 0.75rem;
  color: var(--color-today-border);
  font-weight: 500;
}

.admin-booking-actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 4px;
}


.loading-small {
  padding: 8px;
  color: var(--color-text-muted);
  font-size: 0.85rem;
}


@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* ============================================================
   Credit System Styles
   ============================================================ */

/* Credit badge in header */
.credit-badge {
  display: inline-block;
  color: var(--color-credit);
  font-size: 0.85rem;
  font-weight: 700;
  white-space: nowrap;
  margin-left: 10px;
}

/* Credit info in MyBookings */
.credit-summary {
  background: var(--color-credit-bg);
  border: 1px solid var(--color-credit-border);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
}

.credit-summary strong {
  color: var(--color-credit);
}

/* Sub-tab navigation in MyBookings */
.sub-tab-nav {
  display: flex;
  gap: 16px;
  margin: 16px 0 12px;
  border-bottom: 1px solid var(--color-border);
}

.sub-tab-btn {
  padding: 6px 2px 8px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-size: 0.9rem;
  color: var(--color-text-muted);
  transition: all 0.15s;
}

.sub-tab-btn.active {
  color: var(--color-primary);
  border-bottom-color: var(--color-primary);
  font-weight: 700;
}

/* Credit history in MyBookings */
.credit-history {
  margin-bottom: 20px;
}

.credit-history h3 {
  margin-bottom: 8px;
}

/* Tab "Uplatniť poukaz" */
.redeem-voucher-page {
  max-width: 520px;
}

.redeem-voucher-page h2 {
  margin-bottom: 8px;
}

.redeem-voucher-intro {
  color: var(--color-text-muted);
  font-size: 0.9rem;
  margin: 0 0 16px;
}

.redeem-voucher {
  padding: 16px;
  background: var(--color-credit-bg);
  border-radius: var(--radius);
}

.redeem-voucher-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 6px;
}

.redeem-voucher-row {
  display: flex;
  gap: 8px;
}

.redeem-voucher-row input {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  font-size: 0.9rem;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.redeem-voucher-row input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-focus-ring);
}

.redeem-voucher .form-error,
.redeem-voucher .form-success {
  margin: 8px 0 0;
}

/* Workshop voucher: pick one of the upcoming workshops */
.redeem-workshop-choice {
  margin-top: 20px;
}

.redeem-workshop-choice h3 {
  margin: 0 0 8px;
}

.redeem-workshop-empty {
  color: var(--color-text-muted);
  font-size: 0.9rem;
}

.redeem-workshop-list {
  border-top: 1px solid var(--color-border);
}

.redeem-workshop-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas: "when action" "title action" "free action";
  align-items: center;
  column-gap: 12px;
  row-gap: 2px;
  padding: 10px 4px;
  border-bottom: 1px solid var(--color-border);
  font-size: 0.9rem;
}

.redeem-workshop-when { grid-area: when; }
.redeem-workshop-title { grid-area: title; color: var(--color-text-muted); }
.redeem-workshop-free { grid-area: free; font-size: 0.8rem; color: var(--color-text-muted); }
.redeem-workshop-item .btn { grid-area: action; }

/* Credit row in BookingPopup */
.credit-deduction-row {
  background: var(--color-credit-bg);
  border-radius: 6px;
  padding: 8px 12px;
  margin-top: 8px;
  font-size: 0.9rem;
  color: var(--color-credit);
}

.credit-covered {
  color: var(--color-success);
  font-weight: 700;
}

/* Cancel button in MyBookings */
.booking-cancel-btn {
  background: none;
  border: 1px solid var(--color-danger);
  color: var(--color-danger);
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.15s;
}

.booking-cancel-btn:hover:not(:disabled) {
  background: var(--color-danger);
  color: var(--color-on-primary);
}

.booking-cancel-btn:disabled {
  border-color: var(--color-border);
  color: var(--color-text-muted);
  cursor: not-allowed;
}

/* Wrapper carries the tooltip: a disabled button does not fire mouse events everywhere.
   Pure CSS so it shows instantly; the native title has a browser delay. */
.booking-cancel-locked {
  position: relative;
  display: inline-block;
  cursor: not-allowed;
}

.booking-cancel-locked::after {
  content: attr(data-tooltip);
  position: absolute;
  right: 0;
  bottom: calc(100% + 6px);
  width: max-content;
  max-width: 260px;
  padding: 6px 10px;
  border-radius: 6px;
  background: var(--color-text);
  color: var(--color-surface);
  font-size: 0.78rem;
  line-height: 1.3;
  white-space: normal;
  text-align: left;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.1s;
  z-index: 20;
}

.booking-cancel-locked:hover::after,
.booking-cancel-locked:focus-within::after {
  opacity: 1;
}

/* Cancel confirmation inline */
.cancel-confirm {
  background: var(--color-danger-bg);
  border: 1px solid var(--color-danger-border);
  border-radius: 8px;
  padding: 12px;
  margin-top: 8px;
}

.cancel-confirm p {
  margin: 0 0 8px;
  color: var(--color-danger);
  font-size: 0.9rem;
}

.cancel-confirm-actions {
  display: flex;
  gap: 8px;
}

/* Cancel booking popup */
.popup-eyebrow-mine { color: var(--color-mine); font-weight: 700; }

/* Notification for credit gained */
.credit-notification {
  color: var(--color-success);
  font-weight: 700;
}

/* Booking group card in MyBookings */
.booking-group-card {
  border-bottom: 1px solid var(--color-border);
}

.booking-group-card > .credit-notification,
.booking-group-card > .booking-pay-hint,
.booking-group-card > .booking-pay-error {
  padding: 0 12px 9px;
}

.booking-group-card > .credit-notification { padding-top: 9px; }
.booking-group-card > .cancel-confirm { margin: 0 12px 10px; }

/* ── Admin Credit Management ── */

.admin-credits h2 {
  margin-bottom: 16px;
}

.admin-credit-search {
  margin-bottom: 20px;
}

.admin-credit-search input {
  flex: 1;
}

.admin-customer-list-label {
  font-size: 0.8rem;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 4px 0;
}

.admin-customer-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 12px;
}

.admin-customer-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  font-size: 0.9rem;
  transition: all 0.15s;
}

.admin-customer-item:hover {
  border-color: var(--color-primary);
}

.admin-customer-item.selected {
  border-color: var(--color-primary);
  background: var(--color-primary-soft);
}

.customer-name {
  font-weight: 700;
}

.customer-email {
  color: var(--color-text-muted);
  font-size: 0.85rem;
}

.customer-code-tag {
  background: var(--color-border);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  margin-left: auto;
}

.admin-credit-detail {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 20px;
}

.admin-credit-detail h3 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.customer-email-display {
  color: var(--color-text-muted);
  font-size: 0.9rem;
  margin-bottom: 16px;
}

.admin-credit-balance {
  font-size: 1.1rem;
  margin-bottom: 16px;
  padding: 12px;
  background: var(--color-credit-bg);
  border-radius: 6px;
}

.admin-credit-balance strong {
  color: var(--color-credit);
  font-size: 1.2rem;
}

.admin-adjust-form {
  margin-bottom: 20px;
  padding: 16px;
  background: var(--color-bg);
  border-radius: 8px;
}

.admin-adjust-form .form-field {
  margin-bottom: 10px;
}

.admin-adjust-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.admin-credit-history h4 {
  margin-bottom: 8px;
}

.admin-tx-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.admin-tx-item {
  display: grid;
  grid-template-columns: 140px 80px 80px 1fr;
  gap: 8px;
  align-items: center;
  padding: 6px 8px;
  font-size: 0.85rem;
  border-bottom: 1px solid var(--color-border);
}

.admin-tx-date {
  color: var(--color-text-muted);
}

.admin-tx-type {
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: uppercase;
}

.admin-tx-amount.positive {
  color: var(--color-success);
  font-weight: 700;
}

.admin-tx-amount.negative {
  color: var(--color-danger);
  font-weight: 700;
}

.admin-tx-desc {
  color: var(--color-text-muted);
  font-size: 0.8rem;
}

/* Booking group actions */
/* Pending payment badge */
.booking-pay-hint {
  margin-top: 6px;
  font-size: 0.85rem;
  color: var(--color-text-muted);
}

.booking-pay-error {
  margin-top: 6px;
  font-size: 0.85rem;
  color: var(--color-danger);
}

.pending-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: 700;
  background: var(--color-warning-bg);
  color: var(--color-warning);
  border-radius: 4px;
}

/* PIN status badges (admin) */
.pin-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 4px;
}
.pin-badge.pin-active {
  background: var(--color-success-bg);
  color: var(--color-success);
}
.pin-badge.pin-missing {
  background: var(--color-danger-bg);
  color: var(--color-danger);
}
.pin-badge.pin-unpaid {
  background: var(--color-warning-bg);
  color: var(--color-warning);
}

/* PIN waiting badge (customer Moje rezervácie) */
.pin-waiting-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: 500;
  background: var(--color-warning-bg);
  color: var(--color-warning);
  border-radius: 4px;
  cursor: help;
}

/* Payment status banner */
.payment-banner {
  padding: 0.75rem 1rem;
  margin: 0 1rem;
  border-radius: 8px;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.payment-banner.success {
  background: var(--color-success-bg);
  color: var(--color-success);
}
.payment-banner.warning {
  background: var(--color-warning-bg);
  color: var(--color-warning);
}
.banner-close {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0 0.25rem;
  color: inherit;
  opacity: 0.7;
}
.banner-close:hover {
  opacity: 1;
}

/* ---- Admin: darčekové kupóny ---- */
.admin-vouchers h2 {
  margin-bottom: 16px;
}

.admin-vouchers .admin-card {
  margin-bottom: 20px;
}

.admin-vouchers .input-invalid {
  border-color: var(--color-danger);
}

.admin-voucher-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
}

.admin-voucher-item {
  padding: 10px 12px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 0.9rem;
}

.admin-voucher-item.status-void { opacity: 0.6; }

.admin-voucher-main {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.admin-voucher-code {
  font-weight: 700;
  letter-spacing: 1px;
}

.admin-voucher-value {
  font-weight: 700;
  color: var(--color-credit);
}

.admin-voucher-status {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  background: var(--color-border);
}

.admin-voucher-status.status-active { background: var(--color-success-bg); color: var(--color-success); }
.admin-voucher-status.status-redeemed { background: var(--color-primary-soft); }

.admin-voucher-source {
  margin-left: auto;
  color: var(--color-text-muted);
  font-size: 0.8rem;
}

.admin-voucher-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin-top: 4px;
  color: var(--color-text-muted);
  font-size: 0.8rem;
}

.admin-voucher-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.admin-discount-edit { margin-top: 8px; }

.admin-voucher-confirm {
  font-size: 0.85rem;
}
